import { useCallback, useEffect, useState } from "react";
import { FiChevronLeft, FiChevronRight, FiClock, FiFilm, FiImage, FiRefreshCw, FiCalendar } from "react-icons/fi";
import AdminUserDetail from "../components/AdminUserDetail";
import { getAdminBlockedUploads } from "../api/admin";
import { useTranslation } from "../context/TranslationContext";
import { ADMIN_TONES, adminTheme } from "../styles/adminTheme";

/**
 * Uploads the nudity filter refused, so a false block can be spotted and the thresholds
 * tuned. Only who sent what, where, and how it scored is kept — never the file — so the
 * score and the sender are what there is to judge by.
 *
 * @see mintly-back/src/controllers/adminBlockedUploadsController
 */

const pct = (n) => `${Math.round(Number(n || 0) * 100)}%`;
const bytes = (n) => {
  const v = Number(n || 0);
  if (v >= 1024 * 1024) return `${(v / (1024 * 1024)).toFixed(1)} MB`;
  if (v >= 1024) return `${Math.round(v / 1024)} KB`;
  return `${v} B`;
};

function Count({ theme, icon: Icon, tone, label, value, note }) {
  const colors = ADMIN_TONES[tone] || ADMIN_TONES.mint;
  return (
    <div className={`${theme.cardBase} rounded-2xl p-5`}>
      <div className="flex items-center justify-between gap-3">
        <span className={`text-xs font-medium ${theme.mutedText}`}>{label}</span>
        <span className={`grid h-9 w-9 place-items-center rounded-xl ring-1 ring-inset ${colors.chip}`}>
          <Icon className="h-4 w-4" />
        </span>
      </div>
      <div className={`mt-3 text-3xl font-bold tracking-tight ${theme.baseText}`}>{value}</div>
      {note && <div className={`mt-1 text-xs ${theme.mutedText}`}>{note}</div>}
    </div>
  );
}

const AdminBlockedUploadsTab = ({ isDark, setToast, baseText, mutedText, cardBase }) => {
  const { t, language } = useTranslation();
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState(null);
  const [thresholds, setThresholds] = useState(null);
  const [kind, setKind] = useState("");
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  /** Whose admin detail is open. */
  const [openUser, setOpenUser] = useState(null);

  const theme = adminTheme(isDark);
  const { inputBase, rowBorder } = theme;
  const locale = language === "ka" ? "ka-GE" : "en-GB";
  const when = (iso) => {
    const d = new Date(iso);
    return Number.isNaN(d.getTime())
      ? ""
      : d.toLocaleString(locale, { dateStyle: "medium", timeStyle: "short" });
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getAdminBlockedUploads({ kind: kind || undefined, page, limit: 50 });
      setRows(res.data || []);
      setCounts(res.counts || null);
      setThresholds(res.thresholds || null);
      setPages(res.pages || 1);
      setTotal(res.total || 0);
    } catch (err) {
      setToast?.(err?.response?.data?.message || t("admin.blockedLoadError"));
    } finally {
      setLoading(false);
    }
  }, [kind, page, setToast, t]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="space-y-6">
      {counts && (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
          <Count theme={theme} icon={FiClock} tone="rose" label={t("admin.blockedLast24h")} value={counts.last24h} />
          <Count theme={theme} icon={FiCalendar} tone="amber" label={t("admin.blockedLast7d")} value={counts.last7d} />
          <Count theme={theme} icon={FiImage} tone="aqua" label={t("admin.blockedImages")} value={counts.images} note={t("admin.blockedAllTime")} />
          <Count theme={theme} icon={FiFilm} tone="violet" label={t("admin.blockedVideos")} value={counts.videos} note={t("admin.blockedAllTime")} />
        </div>
      )}

      <div className={`${cardBase} rounded-2xl p-5 sm:p-6`}>
        <div className="flex flex-wrap items-start justify-between gap-4 mb-5">
          <div>
            <h3 className={`text-base font-semibold tracking-tight ${baseText}`}>
              {t("admin.blockedUploads")} ({t("admin.blockedTotal", { count: total })})
            </h3>
            <p className={`text-xs ${mutedText} mt-1 max-w-3xl`}>{t("admin.blockedIntro")}</p>
            {thresholds && (
              <p className={`text-xs ${mutedText} mt-1 max-w-3xl`}>
                {t("admin.blockedThresholds", {
                  image: Math.round(thresholds.image * 100),
                  videoFrame: Math.round(thresholds.videoFrame * 100),
                  videoSuspect: Math.round(thresholds.videoSuspect * 100),
                  videoSuspectFrames: thresholds.videoSuspectFrames,
                })}
              </p>
            )}
          </div>
          <div className="flex items-center gap-3">
            <select
              value={kind}
              onChange={(e) => {
                setPage(1);
                setKind(e.target.value);
              }}
              className={`rounded-xl border px-3 py-2 text-sm outline-none ${inputBase}`}
            >
              <option value="">{t("admin.blockedFilterAll")}</option>
              <option value="image">{t("admin.blockedFilterImages")}</option>
              <option value="video">{t("admin.blockedFilterVideos")}</option>
            </select>
            <button type="button" onClick={load} className={theme.btnSecondary}>
              <FiRefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
              {t("admin.blockedRefresh")}
            </button>
          </div>
        </div>

        <div className={theme.tableWrap}>
          <table className="w-full text-sm">
            <thead>
              <tr className={`text-left ${theme.thead}`}>
                {[
                  t("admin.blockedColWhen"),
                  t("admin.blockedColWho"),
                  t("admin.blockedColWhere"),
                  t("admin.blockedColFile"),
                  t("admin.blockedColKind"),
                  t("admin.blockedColScore"),
                  t("admin.blockedColFrames"),
                ].map((h) => (
                  <th key={h} className={theme.th}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className={`border-t ${rowBorder} ${theme.rowHover} align-top`}>
                  <td className={`px-4 py-3 whitespace-nowrap ${mutedText}`}>{when(r.createdAt)}</td>
                  <td className="px-4 py-3 min-w-[140px]">
                    {r.user ? (
                      <>
                        <button
                          type="button"
                          onClick={() => setOpenUser(r.user.id)}
                          className={`text-left font-semibold ${baseText} hover:text-mint`}
                        >
                          {r.user.username || r.user.email || r.user.id}
                        </button>
                        <div className={`text-[11px] ${mutedText}`}>{r.user.email}</div>
                      </>
                    ) : (
                      <span className={mutedText}>{t("admin.blockedNoUser")}</span>
                    )}
                  </td>
                  <td className="px-4 py-3 min-w-[180px]">
                    <div className={r.mintSpace ? baseText : mutedText}>
                      {r.mintSpace ? r.mintSpace.name || r.mintSpace.id : t("admin.blockedNoSpace")}
                    </div>
                    <div className={`font-mono text-[11px] break-all ${mutedText}`}>{r.route}</div>
                  </td>
                  <td className="px-4 py-3 min-w-[180px] max-w-[280px]">
                    <div className={`break-words ${baseText}`}>{r.fileName || "—"}</div>
                    <div className={`text-[11px] ${mutedText}`}>
                      {[r.mimeType, bytes(r.size)].filter(Boolean).join(" · ")}
                    </div>
                  </td>
                  <td className={`px-4 py-3 whitespace-nowrap ${baseText}`}>
                    {r.kind === "video" ? t("admin.blockedKindVideo") : t("admin.blockedKindImage")}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <span
                      title={t("admin.blockedScoreHint")}
                      className="rounded-lg bg-rose-500/15 px-2 py-0.5 text-xs font-semibold text-rose-400"
                    >
                      {pct(r.score)}
                    </span>
                  </td>
                  <td className={`px-4 py-3 whitespace-nowrap ${baseText}`}>
                    {t("admin.blockedFramesOf", { flagged: r.flagged, frames: r.frames })}
                  </td>
                </tr>
              ))}
              {!loading && rows.length === 0 && (
                <tr>
                  <td colSpan={7} className={`py-10 text-center ${mutedText}`}>
                    {t("admin.blockedNone")}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="flex items-center justify-between mt-4">
          <span className={`text-xs ${mutedText}`}>{t("admin.blockedPage", { page, pages })}</span>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
              className={`${theme.iconBtn} disabled:opacity-40`}
              aria-label={t("common.previous")}
            >
              <FiChevronLeft className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={() => setPage((p) => Math.min(pages, p + 1))}
              disabled={page >= pages}
              className={`${theme.iconBtn} disabled:opacity-40`}
              aria-label={t("common.next")}
            >
              <FiChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>

      {openUser && (
        <AdminUserDetail
          userId={openUser}
          isDark={isDark}
          setToast={setToast}
          onClose={() => setOpenUser(null)}
        />
      )}
    </div>
  );
};

export default AdminBlockedUploadsTab;
